import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { axiosInstance } from '../App';
import { findGuide } from '../lib/guides';

const CARD_WIDTH = 320;
const START_EVENT = 'qflow:start-guide';

// Starts a guide (or the welcome tour) from anywhere: Learn Qflow, Home, Your account.
export const startGuide = (id) => window.dispatchEvent(new CustomEvent(START_EVENT, { detail: { id } }));


const samePlace = (location, path) => `${location.pathname}${location.search}` === path || (!path.includes('?') && location.pathname === path);

// Places the card beside the highlighted element, or bottom-right when there is nothing to point at.
const cardPosition = (rect) => {
  const margin = 16;
  if (!rect) return { right: margin * 2, bottom: margin * 2 };
  const width = Math.min(CARD_WIDTH, window.innerWidth - margin * 2);
  const roomRight = window.innerWidth - rect.right;
  if (roomRight > width + margin * 2) return { left: rect.right + margin, top: Math.max(margin, Math.min(rect.top, window.innerHeight - 260)) };
  const below = rect.bottom + margin;
  const top = below + 220 < window.innerHeight ? below : Math.max(margin, rect.top - 230);
  return { left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)), top };
};

const WelcomeTour = ({ user, setUser }) => {
  // Progress is saved on the user, and Home and Learn Qflow update straight away.
  const saveLearning = (update) => axiosInstance.put('/users/me/learning', update).then(({ data }) => {
    window.dispatchEvent(new CustomEvent('qflow:learning-updated', { detail: data }));
    if (setUser) setUser((current) => (current ? { ...current, learning: data } : current));
  }).catch(() => {});
  const navigate = useNavigate();
  const location = useLocation();
  const [guide, setGuide] = useState(null);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);

  const begin = useCallback((id, at = 0) => {
    const found = findGuide(user, id);
    if (!found?.steps?.length) return;
    setGuide(found);
    setIndex(Math.min(at, found.steps.length - 1));
  }, [user]);

  // Until the welcome tour is finished or closed, it starts (where the person left it)
  // the first time they land on Home in a session, so it never pulls them off a link they opened.
  const autoStarted = useRef(false);
  useEffect(() => {
    const learning = user?.learning || {};
    if (autoStarted.current || location.pathname !== '/') return undefined;
    if (user?.onboarding_status && user.onboarding_status !== 'active') return undefined;
    if (learning.tour_done_at) return undefined;
    const timer = setTimeout(() => { autoStarted.current = true; begin('welcome', learning.tour_step || 0); }, 800);
    return () => clearTimeout(timer);
  }, [user?.id, location.pathname]);

  useEffect(() => {
    const onStart = (event) => begin(event.detail?.id || 'welcome');
    window.addEventListener(START_EVENT, onStart);
    return () => window.removeEventListener(START_EVENT, onStart);
  }, [begin]);

  const step = guide?.steps[index];

  // Open the step's page, then follow the target element while the page settles.
  useEffect(() => {
    if (step?.path && !samePlace(location, step.path)) navigate(step.path);
  }, [step]);

  useLayoutEffect(() => {
    if (!step) return undefined;
    let frame;
    let tries = 0;
    let scrolled = false;
    const measure = (poll = true) => {
      const element = step.target ? document.querySelector(step.target) : null;
      if (element) {
        const box = element.getBoundingClientRect();
        if (!scrolled) { scrolled = true; element.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        setRect(box.width ? { top: box.top, left: box.left, width: box.width, height: box.height, right: box.right, bottom: box.bottom } : null);
      } else {
        setRect(null);
      }
      if (!poll) return;
      tries += 1;
      if (tries < 40) frame = window.setTimeout(measure, 150);
    };
    measure();
    // Keep the ring on its element while the page or the main panel scrolls.
    const onMove = () => measure(false);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => { window.clearTimeout(frame); window.removeEventListener('resize', onMove); window.removeEventListener('scroll', onMove, true); };
  }, [step, location.pathname, location.search]);

  const close = (finished) => {
    if (guide?.id === 'welcome') saveLearning({ tour_done: true });
    else if (finished && guide) saveLearning({ guide_done: guide.id });
    setGuide(null);
    setRect(null);
  };

  const go = (next) => {
    if (next >= guide.steps.length) { close(true); return; }
    setIndex(next);
    if (guide.id === 'welcome') saveLearning({ tour_step: next });
  };

  useEffect(() => {
    if (!guide) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') close(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!guide || !step) return null;
  const last = index === guide.steps.length - 1;
  const nextStep = guide.steps[index + 1];
  const pad = 6;

  return (
    <div className="qflow-tour" data-testid="welcome-tour" aria-live="polite">
      {rect && <div className="qflow-tour-ring" style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} aria-hidden="true" />}
      <div role="dialog" aria-label={`${guide.title}: ${step.title}`} className="qflow-tour-card" style={{ width: Math.min(CARD_WIDTH, window.innerWidth - 32), ...cardPosition(rect) }}>
        <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide">
          <span className="qflow-accent-text">{guide.title} · {index + 1} of {guide.steps.length}</span>
          <button type="button" onClick={() => close(false)} className="qflow-tour-muted hover:underline" data-testid="tour-skip" aria-label="Close tour"><X size={14} /></button>
        </div>
        <h2 className="mt-1.5 text-[15px] font-bold">{step.title}</h2>
        <p className="qflow-tour-muted mt-1 text-[13px] leading-relaxed">{step.body}</p>
        {nextStep && nextStep.path !== step.path && <p className="qflow-tour-muted mt-2 text-[11px] font-semibold">Up next: {nextStep.title}</p>}
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="flex gap-1" aria-hidden="true">{guide.steps.map((item, position) => <span key={position} className={`h-1 w-3.5 rounded-full ${position <= index ? 'qflow-accent-bg' : 'qflow-tour-dot'}`} />)}</span>
          <span className="flex gap-2">
            {index > 0 && <button type="button" onClick={() => go(index - 1)} className="qflow-tour-muted px-2 text-[13px] font-semibold hover:underline" data-testid="tour-back">Back</button>}
            <button type="button" onClick={() => go(index + 1)} className="qflow-accent-bg whitespace-nowrap rounded-lg px-3 py-1.5 text-[13px] font-semibold" data-testid="tour-next">{last ? 'Finish' : 'Next'}</button>
          </span>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
